<template>
  <div>
    <DashboardFallback
      v-if="!hasDashboardWidgets"
      :name="accountName"
      :email="accountEmail"
      :shortcuts="moduleShortcuts"
    />
    <div v-if="metrics.length" class="row">
      <div
        v-for="metric in metrics"
        :key="metric.key"
        class="col-12 col-md mb-3"
      >
        <StatisticCard
          :title="metric.title"
          :value="metric.value"
          :icon="metric.icon"
          :loading="metric.loading"
          :error="metric.error"
          :subtitle="metric.key === 'candidates' ? candidates.month : ''"
          @retry="retryMetric(metric.key)"
        />
      </div>
    </div>
    <div v-if="hasDashboardWidgets" class="row">
      <div
        v-if="canViewLockedAppUsers"
        class="col-12 mb-3"
        :class="{
          'col-xl-6': canViewLockedPortalUsers || canViewPendingAppUsers,
        }"
      >
        <LockedAppUsers
          :widget="lockedApp"
          :busy="unlockPending.app"
          :permission="permissions.app_users || 0"
          @unlock="confirmUnlock($event, 'app')"
          @retry="loadUserList('lockedApp')"
          @page="loadUserList('lockedApp', $event)"
        />
      </div>
      <div
        v-if="canViewLockedPortalUsers"
        class="col-12 mb-3"
        :class="{ 'col-xl-6': canViewLockedAppUsers || canViewPendingAppUsers }"
      >
        <LockedPortalUsers
          :widget="lockedPortal"
          :busy="unlockPending.portal"
          :permission="permissions.portal_users || 0"
          @unlock="confirmUnlock($event, 'portal')"
          @retry="loadUserList('lockedPortal')"
          @page="loadUserList('lockedPortal', $event)"
        />
      </div>
      <div
        v-if="canViewPendingAppUsers"
        class="col-12 mb-3"
        :class="{
          'col-xl-6': canViewLockedAppUsers !== canViewLockedPortalUsers,
        }"
      >
        <PendingAppUsers
          :widget="pending"
          :busy="approvalPending"
          :permission="permissions.app_users || 0"
          @approve="confirmApproveAppUser"
          @reject="confirmRejectAppUser"
          @retry="loadUserList('pending')"
          @page="loadUserList('pending', $event)"
        />
      </div>
      <div v-if="(permissions.article_approval || 0) >= 1" class="col-12 mb-3">
        <PendingArticles :permission="permissions.article_approval" />
      </div>
      <div v-if="(permissions.event_admin || 0) >= 1" class="col-12 mb-3">
        <UpcomingEvents :permission="permissions.event_admin" />
      </div>
      <div v-if="(permissions.developer || 0) >= 1" class="col-12 mb-3">
        <NewReports :permission="permissions.developer" />
      </div>
    </div>
  </div>
</template>
<script>
import StatisticCard from "./StatisticCard.vue";
import LockedAppUsers from "./LockedAppUsers.vue";
import LockedPortalUsers from "./LockedPortalUsers.vue";
import PendingAppUsers from "./PendingAppUsers.vue";
import PendingArticles from "./PendingArticles.vue";
import UpcomingEvents from "./UpcomingEvents.vue";
import NewReports from "./NewReports.vue";
import DashboardFallback from "./DashboardFallback.vue";
import { getDashboardShortcuts } from "./module-shortcuts";
import { dashboardService } from "@/services/api/dashboard.service";
import { userService } from "@/services/api/user.service";
import { alertHelper, sharedHelper, sessionHelper } from "@/utils";
const userWidget = () => ({
  users: [],
  total: 0,
  page: 1,
  pageSize: 15,
  loading: true,
  error: false,
  requestId: 0,
});
const isEnabled = (value) => [true, 1, "1"].includes(value);
export default {
  name: "DashboardContent",
  components: {
    StatisticCard,
    LockedAppUsers,
    LockedPortalUsers,
    PendingAppUsers,
    PendingArticles,
    UpcomingEvents,
    NewReports,
    DashboardFallback,
  },
  data() {
    return {
      candidates: {
        count: 0,
        month: "",
        loading: true,
        error: false,
        requestId: 0,
      },
      lockedApp: userWidget(),
      lockedPortal: userWidget(),
      pending: userWidget(),
      unlockPending: { app: false, portal: false },
      approvalPending: false,
    };
  },
  computed: {
    hasDashboardWidgets() {
      return (
        this.metrics.length > 0 ||
        (this.permissions.article_approval || 0) >= 1 ||
        (this.permissions.event_admin || 0) >= 1 ||
        (this.permissions.developer || 0) >= 1
      );
    },
    moduleShortcuts() {
      return getDashboardShortcuts(this.permissions);
    },
    accountName() {
      const user = this.$store.state.api.auth.user?.user;
      return (
        [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
        sessionHelper.getUserName() ||
        ""
      );
    },
    accountEmail() {
      return this.$store.state.api.auth.user?.user?.email || "";
    },
    permissions() {
      const user = this.$store.state.api.auth.user?.user;
      return sessionHelper.getUserPermissions(
        user?.permissions,
        user?.isSuperadmin,
      );
    },
    canViewLockedAppUsers() {
      return (this.permissions.app_users || 0) >= 1;
    },
    canViewLockedPortalUsers() {
      return (this.permissions.portal_users || 0) >= 1;
    },
    canViewPendingAppUsers() {
      return (this.permissions.app_users || 0) >= 1;
    },
    canViewCandidates() {
      return (this.permissions.candidate_admin || 0) >= 1;
    },
    metrics() {
      return [
        {
          key: "candidates",
          title: this.$t("label.dashboardMonthlyCandidates"),
          value: this.candidates.count,
          icon: "mdi-account-tie",
          loading: this.candidates.loading,
          error: this.candidates.error,
        },
        {
          key: "lockedApp",
          title: this.$t("label.dashboardLockedAppUsers"),
          value: this.lockedApp.total,
          icon: "mdi-account-lock",
          loading: this.lockedApp.loading,
          error: this.lockedApp.error,
        },
        {
          key: "lockedPortal",
          title: this.$t("label.dashboardLockedPortalUsers"),
          value: this.lockedPortal.total,
          icon: "mdi-account-lock-outline",
          loading: this.lockedPortal.loading,
          error: this.lockedPortal.error,
        },
        {
          key: "pending",
          title: this.$t("label.dashboardPendingAppUsers"),
          value: this.pending.total,
          icon: "mdi-account-clock-outline",
          loading: this.pending.loading,
          error: this.pending.error,
        },
      ].filter(
        (metric) =>
          (metric.key !== "candidates" || this.canViewCandidates) &&
          (metric.key !== "lockedApp" || this.canViewLockedAppUsers) &&
          (metric.key !== "lockedPortal" || this.canViewLockedPortalUsers) &&
          (metric.key !== "pending" || this.canViewPendingAppUsers),
      );
    },
  },
  watch: {
    canViewCandidates() {
      this.loadCandidateCount();
    },
    canViewLockedAppUsers() {
      this.loadUserList("lockedApp");
    },
    canViewLockedPortalUsers() {
      this.loadUserList("lockedPortal");
    },
    canViewPendingAppUsers() {
      this.loadUserList("pending");
    },
  },
  created() {
    this.loadCandidateCount();
    this.loadUserList("lockedApp");
    this.loadUserList("lockedPortal");
    this.loadUserList("pending");
  },
  beforeDestroy() {
    this.candidates.requestId++;
    this.lockedApp.requestId++;
    this.lockedPortal.requestId++;
    this.pending.requestId++;
  },
  methods: {
    confirmApproveAppUser(user) {
      return this.confirmAppAccessDecision(user, 1);
    },
    confirmRejectAppUser(user) {
      return this.confirmAppAccessDecision(user, 0);
    },
    async confirmAppAccessDecision(user, appAccess) {
      if (![0, 1].includes(appAccess)) return;
      if (sessionHelper.getPermissionLevel("app_users") !== 2) return;
      if (this.approvalPending || Number(user.appAccess) !== 2) return;
      const action = appAccess === 1 ? "Approve" : "Reject";
      this.approvalPending = true;
      try {
        const confirmed = await this.$bvModal.msgBoxConfirm(
          this.$t(`label.dashboardConfirm${action}`, [user.name]),
          {
            title: this.$t(`label.dashboard${action}`),
            okTitle: this.$t(`label.dashboard${action}`),
            cancelTitle: this.$t("label.cancel"),
            okVariant: appAccess === 1 ? "info" : "danger",
            centered: true,
          },
        );
        if (!confirmed) return;
        if (sessionHelper.getPermissionLevel("app_users") !== 2) return;
        const result =
          appAccess === 1
            ? await userService.approveAppAccess(user.uuid)
            : await userService.updateAppAccess(user.uuid, 0);
        if (!result?.success)
          throw new Error(
            result?.message || this.$t(`label.dashboard${action}Error`),
          );
        alertHelper.alertSuccess({
          message: this.$t(`label.dashboard${action}Success`, [user.name]),
        });
        await this.loadUserList("pending");
        if (
          !this.pending.error &&
          !this.pending.users.length &&
          this.pending.page > 1
        ) {
          await this.loadUserList(
            "pending",
            Math.max(1, Math.ceil(this.pending.total / this.pending.pageSize)),
          );
        }
      } catch (error) {
        alertHelper.alertError({
          message:
            sharedHelper.getErrorMessage(error) ||
            this.$t(`label.dashboard${action}Error`),
          title: this.$t("message.error"),
        });
      } finally {
        this.approvalPending = false;
      }
    },
    async confirmUnlock(user, access) {
      if (!["app", "portal"].includes(access) || this.unlockPending[access])
        return;
      const permissionKey = access === "app" ? "app_users" : "portal_users";
      if (sessionHelper.getPermissionLevel(permissionKey) !== 2) return;
      if (!user.hasAccess || !user.isLocked) return;
      this.unlockPending[access] = true;
      try {
        const accessName = this.$t(
          access === "app"
            ? "label.dashboardAppAccess"
            : "label.dashboardPortalAccess",
        );
        const confirmed = await this.$bvModal.msgBoxConfirm(
          this.$t("label.dashboardConfirmUnlock", [accessName, user.name]),
          {
            title: this.$t("label.dashboardUnlock"),
            okTitle: this.$t("label.dashboardUnlock"),
            cancelTitle: this.$t("label.cancel"),
            okVariant: "info",
            centered: true,
          },
        );
        if (!confirmed) return;
        // Recheck the current session permissions after confirmation.
        if (sessionHelper.getPermissionLevel(permissionKey) !== 2) return;
        const result = await userService.unlock({
          uuid: user.uuid,
          isApp: access === "app",
          isPortal: access === "portal",
        });
        if (!result?.success)
          throw new Error(
            result?.message || this.$t("label.dashboardUnlockError"),
          );
        alertHelper.alertSuccess({
          message: this.$t("label.dashboardUnlockSuccess", [
            accessName,
            user.name,
          ]),
        });
        const key = access === "app" ? "lockedApp" : "lockedPortal";
        await this.loadUserList(key);
        const widget = this[key];
        if (!widget.error && !widget.users.length && widget.page > 1) {
          await this.loadUserList(
            key,
            Math.max(1, Math.ceil(widget.total / widget.pageSize)),
          );
        }
      } catch (error) {
        alertHelper.alertError({
          message:
            sharedHelper.getErrorMessage(error) ||
            this.$t("label.dashboardUnlockError"),
          title: this.$t("message.error"),
        });
      } finally {
        this.unlockPending[access] = false;
      }
    },
    retryMetric(key) {
      return key === "candidates"
        ? this.loadCandidateCount()
        : this.loadUserList(key);
    },
    async loadCandidateCount() {
      const widget = this.candidates,
        requestId = ++widget.requestId;
      if (!this.canViewCandidates) {
        widget.count = 0;
        widget.month = "";
        widget.loading = false;
        widget.error = false;
        return;
      }
      widget.loading = true;
      widget.error = false;
      try {
        const result = await dashboardService.getCandidateCount(),
          count = result?.data?.candidate_count;
        if (
          !result?.success ||
          count == null ||
          !Number.isFinite(Number(count)) ||
          Number(count) < 0
        )
          throw new Error("Invalid candidate count.");
        if (requestId !== widget.requestId) return;
        widget.count = Number(count);
        widget.month = result.data.month || "";
      } catch (error) {
        if (requestId === widget.requestId) widget.error = true;
      } finally {
        if (requestId === widget.requestId) widget.loading = false;
      }
    },
    async loadUserList(key, page = this[key].page) {
      const widget = this[key],
        requestId = ++widget.requestId;
      if (
        (key === "lockedApp" && !this.canViewLockedAppUsers) ||
        (key === "lockedPortal" && !this.canViewLockedPortalUsers) ||
        (key === "pending" && !this.canViewPendingAppUsers)
      ) {
        widget.users = [];
        widget.total = 0;
        widget.page = 1;
        widget.loading = false;
        widget.error = false;
        return;
      }
      widget.loading = true;
      widget.error = false;
      try {
        const method = {
          lockedApp: "getLockedAppUsers",
          lockedPortal: "getLockedPortalUsers",
          pending: "getPendingAppUsers",
        }[key];
        const result = await dashboardService[method](page, widget.pageSize);
        if (!result?.success || !Array.isArray(result.data))
          throw new Error("Invalid user list.");
        if (requestId !== widget.requestId) return;
        widget.users = result.data.map((user) => ({
          uuid: user.uuid,
          name:
            user.name ||
            [user.first_name, user.last_name].filter(Boolean).join(" "),
          email: user.email || "—",
          isLocked: isEnabled(user.is_locked),
          hasAccess: isEnabled(
            key === "lockedApp" ? user.app_access : user.portal_access,
          ),
          appAccess: user.app_access,
        }));
        widget.total = result.pagination?.total ?? result.data.length;
        widget.page = result.pagination?.current_page || page;
        widget.pageSize = result.pagination?.per_page || widget.pageSize;
      } catch (error) {
        if (requestId === widget.requestId) widget.error = true;
      } finally {
        if (requestId === widget.requestId) widget.loading = false;
      }
    },
  },
};
</script>
